import type { Space } from '@cocoonspace/sdk-js/types/space'
import { type ComponentProps, memo, useMemo } from 'react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Button } from '@cocoonspace/ui/components/button'
import { Card, cardVariants } from '@cocoonspace/ui/components/card'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import dynamic from 'next/dynamic'
import Link from 'next/link'

import { useCart } from '~/domains/cart/cart-provider'
import { useAddToCart } from '~/domains/cart/use-add-to-cart.hook'
import { BookingOption } from '~/domains/cart/use-add-to-cart.hook'
import { SpaceCardNearestStation } from '~/domains/search/components/space-card/space-card-nearest-station'
import { SpaceCardPrice } from '~/domains/search/components/space-card/space-card-price'
import { SpaceCapacity } from '~/domains/spaces/components/atoms/space-capacity'
import { SpaceFavoriteButton } from '~/domains/spaces/components/atoms/space-favorite-button'
import { SpaceSurface } from '~/domains/spaces/components/atoms/space-surface'
import { useAuth } from '~/providers/auth-provider'
// import { HasExperiment } from '~/hooks/use-experiment'
import { getDayValueAsArray } from '../filters/timeslot-filter'

const SpaceCardCarousel = dynamic(
	() =>
		import('~/domains/spaces/components/organisms/space-card-carousel').then(
			(comp) => comp.SpaceCardCarousel,
		),
	{
		loading: () => (
			<div className='aspect-[366/175] w-full animate-pulse bg-slate-200' />
		),
		ssr: false,
	},
)

export const SpaceCornerPill = ({
	space,
}: {
	space: Pick<
		Space,
		'rebate_percent' | 'new' | 'prices_starting_at' | 'opening_date'
	>
}) => {
	const { t } = useLingui()

	const content = useMemo(() => {
		const openingDate = dateTimeUtils(space.opening_date, 'YYYY-MM-DD')

		if (openingDate.isAfter(dateTimeUtils())) {
			const date = openingDate.customFormat('dateMonthDay')
			return t`Ouverture le ${date}`
		}
		if (space.new) {
			return t`Nouveau`
		}
		if (!space.prices_starting_at && !!space.rebate_percent) {
			return `-${space.rebate_percent}%`
		}

		return ''
	}, [space, t])

	if (!content) return null

	return (
		<Card
			data-testid='space-corner-pill'
			className='text-foreground absolute left-4 top-4 z-10 flex min-h-[32px] items-center gap-1 rounded-md bg-white/70 p-2 px-3 text-sm'
		>
			{content}
		</Card>
	)
}

const SpaceDistance = ({ distance }: { distance?: number }) => {
	const { t } = useLingui()

	if (!distance) return null

	return (
		<span
			className='mb-px ml-1 whitespace-nowrap text-xs text-slate-500'
			data-testid='space-distance'
		>
			{t`À ${distance}m`}
		</span>
	)
}

const AddToCartButton = ({
	space,
	timeSlot,
}: {
	space: Space
	timeSlot: {
		day: string[]
		start?: string
		end?: string
	}
}) => {
	const { t } = useLingui()
	const { setIsSheetOpen } = useCart()

	const { mutateAsync: addToCart, isPending } = useAddToCart({ space })

	const bookingOptions = useMemo(() => {
		if (!timeSlot.day.length) return []

		if (timeSlot.day.length > 1) {
			return timeSlot.day.map((day) => ({ day }))
		}

		return [
			{
				day: timeSlot.day[0],
				start: timeSlot.start,
				end: timeSlot.end,
			},
		] as BookingOption[]
	}, [timeSlot.day, timeSlot.start, timeSlot.end])

	const humanReadableDate = useMemo(() => {
		if (!timeSlot.day.length) return ''

		const days = getDayValueAsArray(timeSlot.day)?.map((day) =>
			dateTimeUtils(day).format('D MMM'),
		)

		if (timeSlot.start && timeSlot.end) {
			const start = timeSlot.start?.replace(':', 'h').replace('h00', 'h')
			const end = timeSlot.end?.replace(':', 'h').replace('h00', 'h')

			return `${days?.[0]}, ${start}-${end}`
		}

		return `${days?.join(', ')}`
	}, [timeSlot.day, timeSlot.start, timeSlot.end])

	if (!timeSlot.day.length) return null

	return (
		<Button
			variant='gradient'
			size='sm'
			className='mx-4 mb-4'
			disabled={isPending}
			onClick={(e) => {
				e.preventDefault()
				e.stopPropagation()
				addToCart({ bookingOptions }).then(() => setIsSheetOpen(true))
			}}
		>
			{t`Ajouter au panier: ${humanReadableDate}`}
		</Button>
	)
}

export const SpaceCard = memo(
	({
		space,
		showDistance,
		href,
		draggable = true,
		className,
		hidePrices = false,
		hideFavBtn = false,
		timeSlot,
		...props
	}: {
		space: Space
		showDistance?: boolean
		href: Parameters<typeof Link>[0]['href']
		draggable?: boolean
		className?: string
		onMouseEnter?: () => void
		onMouseLeave?: () => void
		onClick?: () => void
		hidePrices?: boolean
		hideFavBtn?: boolean
		timeSlot?: {
			day: string[]
			start?: string
			end?: string
		}
	} & ComponentProps<typeof Link>) => {
		const { isAuth } = useAuth()

		return (
			<Link
				{...props}
				className={cn(
					cardVariants({
						className:
							'relative z-0 flex cursor-pointer flex-col overflow-hidden rounded-md transition-shadow duration-200 hover:ring-[3px] hover:ring-primary',
					}),
					className,
				)}
				href={href}
				target='_blank'
				title={space.title}
			>
				<SpaceCornerPill space={space} />

				{!hideFavBtn && (
					<Card className='text-foreground absolute right-4 top-4 z-10 flex gap-1 rounded-md bg-white/70 p-1'>
						{!hideFavBtn && <SpaceFavoriteButton space={space} />}
					</Card>
				)}

				<SpaceCardCarousel
					draggable={draggable}
					space={space}
				/>

				<div className='relative grid grid-cols-4 px-4 py-2'>
					<div className='col-span-3 flex flex-col gap-0.5'>
						<div className='flex flex-row items-end font-bold leading-none'>
							<div className='truncate'>{space.title}</div>
							{showDistance && <SpaceDistance distance={space.distance} />}
						</div>

						<SpaceCardNearestStation space={space} />

						<div className='flex flex-row gap-3 text-xs'>
							<SpaceCapacity
								className='text-xs'
								space={space}
							/>

							<SpaceSurface
								className='text-xs'
								space={space}
							/>
						</div>
					</div>

					{!hidePrices && (
						<div className='col-span-1 flex flex-col items-end justify-center'>
							<SpaceCardPrice space={space} />
						</div>
					)}
				</div>

				{/* <HasExperiment experimentId='carts-beta'> */}
				{!!timeSlot && isAuth && (
					<AddToCartButton
						space={space}
						timeSlot={timeSlot}
					/>
				)}
				{/* </HasExperiment> */}
			</Link>
		)
	},
)

export const SpaceCardSkeleton = ({ className }: { className?: string }) => (
	<Card className={className}>
		<Skeleton className='aspect-[366/175] w-full rounded-b-none' />

		<div className='p-4'>
			<Skeleton className='mb-2 h-[16px] w-[200px]' />
			<Skeleton className='mb-2 h-[12px] w-[160px]' />
			<Skeleton className='h-[12px] w-[100px]' />
		</div>
	</Card>
)
